import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import adminService from '@wlb/shared/api/admin/adminService';
import type { DashboardNew } from '@wlb/shared/api/admin/adminTypes';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Card } from '../components/ui/Card';
import { DataTable, type DataTableColumn } from '../components/ui/DataTable';
import { FormError } from '../components/ui/FormError';
import type { IconName } from '../components/ui/Icon';
import { IconButton } from '../components/ui/IconButton';
import { PageHeader } from '../components/layout/PageHeader';
import { Skeleton } from '../components/ui/Skeleton';
import { StateBlock } from '../components/ui/StateBlock';
import { TextField } from '../components/ui/TextField';
import { InfoTile } from '../features/dashboard/InfoTile';
import { findMetric } from '../features/dashboard/metricTotal';
import { PlatformBreakdown } from '../features/dashboard/PlatformBreakdown';
import { StatTile, type StatTone } from '../features/dashboard/StatTile';
import { useAsyncResource } from '../hooks/useAsyncResource';
import {
  dateInputToIsoEnd,
  dateInputToIsoStart,
  daysAgoInputValue,
  formatCount,
  isValidDateRange,
  toDateInputValue,
} from '../utils/format';
import '../components/ui/ui.css';
import '../features/dashboard/dashboard.css';
import './pages.css';

type MetricKey = keyof Pick<
  DashboardNew,
  | 'allUsers'
  | 'activeUsers'
  | 'newUsersInDateRange'
  | 'returnUsersInDateRange'
  | 'userLoggedInDateRange'
  | 'totalPostsInDateRange'
  | 'uniquePostsInDateRange'
  | 'totalUploadPhotosInDateRange'
  | 'totalcomments'
  | 'totalshares'
  | 'usersloggedIntoday'
  | 'usersloggedInYesterday'
  | 'usersloggedInthisMonth'
  | 'usersLoggedinpast30days'
  | 'totalposts'
  | 'uniqueposts'
  | 'newusers'
  | 'returnusers'
>;

/** The four tiles that lead the page, each with a drill-down. */
const HEADLINE: readonly {
  /** A DashboardNew counter, or 'downloads' — that one lives in metrics[]. */
  key: MetricKey | 'downloads';
  icon: IconName;
  tone: StatTone;
  to: string;
  linkKey: string;
}[] = [
  {
    key: 'downloads',
    icon: 'download',
    tone: 'primary',
    // Downloads has no detail page — its breakdown is the platform card
    // below, so the tile anchors to it instead of an unrelated screen.
    to: '#dash-platform',
    linkKey: 'dashboard.links.platform',
  },
  {
    key: 'newUsersInDateRange',
    icon: 'userPlus',
    tone: 'info',
    to: '/users',
    linkKey: 'dashboard.links.users',
  },
  {
    key: 'totalPostsInDateRange',
    icon: 'post',
    tone: 'warning',
    to: '/posts',
    linkKey: 'dashboard.links.posts',
  },
  {
    key: 'uniquePostsInDateRange',
    icon: 'userCheck',
    tone: 'success',
    // Posters, not posts: drill into the member-activity ranking.
    to: '/statistics',
    linkKey: 'dashboard.links.statistics',
  },
];

/** Sign-in counters, shown as a share of the whole membership. */
const AUDIENCE: readonly { key: MetricKey; icon: IconName }[] = [
  { key: 'usersloggedIntoday', icon: 'clock' },
  { key: 'usersloggedInYesterday', icon: 'calendar' },
  { key: 'usersloggedInthisMonth', icon: 'calendar' },
  { key: 'usersLoggedinpast30days', icon: 'trendUp' },
];

/** Period counters with no meaningful denominator, so no meter. Unique
 * posters is absent here on purpose — it is one of the headline tiles. */
const ENGAGEMENT: readonly { key: MetricKey; icon: IconName }[] = [
  { key: 'userLoggedInDateRange', icon: 'activity' },
  { key: 'returnUsersInDateRange', icon: 'users' },
  { key: 'totalUploadPhotosInDateRange', icon: 'image' },
  { key: 'totalcomments', icon: 'comment' },
  { key: 'totalshares', icon: 'share' },
];

/** Lifetime counters the API reports alongside the in-range ones. */
const ALL_TIME: readonly { key: MetricKey; icon: IconName }[] = [
  { key: 'totalposts', icon: 'post' },
  { key: 'uniqueposts', icon: 'users' },
  { key: 'newusers', icon: 'userPlus' },
  { key: 'returnusers', icon: 'userCheck' },
];

const RANGE_PRESETS: readonly { days: number; labelKey: string }[] = [
  { days: 7, labelKey: 'dashboard.range.last7' },
  { days: 30, labelKey: 'dashboard.range.last30' },
  { days: 90, labelKey: 'dashboard.range.last90' },
];

const SHORTCUTS: readonly { to: string; labelKey: string }[] = [
  { to: '/moderation', labelKey: 'dashboard.shortcuts.moderation' },
  { to: '/users', labelKey: 'dashboard.shortcuts.users' },
  { to: '/agents', labelKey: 'dashboard.shortcuts.agents' },
  { to: '/affiliates', labelKey: 'dashboard.shortcuts.affiliates' },
];

export function Dashboard() {
  const { t } = useTranslation('admin');
  const [startDate, setStartDate] = useState(() => daysAgoInputValue(30));
  const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));

  const load = useCallback(
    () =>
      adminService.getDashboardMetrics({
        startDate: dateInputToIsoStart(startDate),
        endDate: dateInputToIsoEnd(endDate),
      }),
    [endDate, startDate],
  );

  const rangeValid = isValidDateRange(startDate, endDate);

  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    `${startDate}|${endDate}`,
    t('common.errorTitle'),
    rangeValid,
    'dashboard',
  );

  const today = toDateInputValue(new Date());

  const applyPreset = useCallback((days: number) => {
    setStartDate(daysAgoInputValue(days));
    setEndDate(toDateInputValue(new Date()));
  }, []);

  const activePreset = useMemo(() => {
    if (endDate !== today) {
      return null;
    }
    return (
      RANGE_PRESETS.find(preset => daysAgoInputValue(preset.days) === startDate)
        ?.days ?? null
    );
  }, [endDate, startDate, today]);

  const platformColumns = useMemo<
    readonly DataTableColumn<NonNullable<DashboardNew['metrics']>[number]>[]
  >(
    () => [
      {
        key: 'metric',
        header: t('dashboard.platform.metric'),
        render: row => row.metricname || row.metrickey || t('common.none'),
      },
      {
        key: 'desktop',
        header: t('dashboard.platform.desktop'),
        numeric: true,
        render: row => formatCount(row.desktop),
      },
      {
        key: 'android',
        header: t('dashboard.platform.android'),
        numeric: true,
        render: row => formatCount(row.android),
      },
      {
        key: 'ios',
        header: t('dashboard.platform.ios'),
        numeric: true,
        render: row => formatCount(row.ios),
      },
      {
        key: 'total',
        header: t('dashboard.platform.total'),
        numeric: true,
        render: row => formatCount(row.desktop + row.android + row.ios),
      },
    ],
    [t],
  );

  const metrics = useMemo(() => data?.metrics ?? [], [data]);

  // Downloads is a per-platform metric row, not a scalar counter. The tile
  // leads with the total and lists each platform with downloads underneath.
  const downloadsRow = useMemo(() => findMetric(metrics, 'downloads'), [metrics]);
  const downloadsTotal = downloadsRow
    ? downloadsRow.desktop + downloadsRow.android + downloadsRow.ios
    : 0;
  const downloadsHint = useMemo(() => {
    if (!downloadsRow) return '';
    return (['desktop', 'android', 'ios'] as const)
      .filter(platform => downloadsRow[platform] > 0)
      .map(platform => `${t(`dashboard.platform.${platform}`)} ${formatCount(downloadsRow[platform])}`)
      .join(' · ');
  }, [downloadsRow, t]);

  return (
    <div className="page">
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        actions={
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            disabled={refreshing || !rangeValid}
            onClick={reload}
          />
        }
      />

      <section className="page__toolbar" aria-label={t('dashboard.rangeLabel')}>
        <div
          className="dash-presets"
          role="group"
          aria-label={t('dashboard.range.presetLabel')}>
          {RANGE_PRESETS.map(preset => (
            <button
              className="dash-presets__button"
              key={preset.days}
              type="button"
              aria-pressed={activePreset === preset.days}
              onClick={() => applyPreset(preset.days)}>
              {t(preset.labelKey)}
            </button>
          ))}
        </div>
        <div className="page__toolbar-field">
          <TextField
            label={t('dashboard.startDate')}
            type="date"
            value={startDate}
            max={endDate}
            onChange={event => setStartDate(event.target.value)}
          />
        </div>
        <div className="page__toolbar-field">
          <TextField
            label={t('dashboard.endDate')}
            type="date"
            value={endDate}
            min={startDate}
            onChange={event => setEndDate(event.target.value)}
          />
        </div>
      </section>

      <FormError variant="banner" message={rangeValid ? '' : t('dashboard.rangeInvalid')} />

      {status === 'loading' ? (
        <section
          className="dash-stats"
          aria-busy="true"
          aria-label={t('dashboard.sections.headline')}>
          {HEADLINE.map(tile => (
            <div className="dash-stat dash-stat--skeleton" key={tile.key}>
              <Skeleton height="14px" width="55%" />
              <Skeleton height="30px" width="40%" />
              <Skeleton height="12px" width="30%" />
            </div>
          ))}
        </section>
      ) : null}
      {status === 'error' ? (
        <StateBlock status="error" message={error} onRetry={reload} />
      ) : null}

      {status === 'ready' && data ? (
        <div className="page__stack" aria-busy={refreshing || undefined}>
          <section className="dash-stats" aria-label={t('dashboard.sections.headline')}>
            {HEADLINE.map(tile => (
              <StatTile
                key={tile.key}
                icon={tile.icon}
                label={t(`dashboard.metrics.${tile.key}`)}
                hint={tile.key === 'downloads' ? downloadsHint : undefined}
                linkLabel={t(tile.linkKey)}
                to={tile.to}
                tone={tile.tone}
                value={formatCount(tile.key === 'downloads' ? downloadsTotal : data[tile.key])}
              />
            ))}
          </section>

          <Card
            id="dash-platform"
            subtitle={t('dashboard.platform.subtitle')}
            title={t('dashboard.platform.title')}>
            {metrics.length > 0 ? (
              <>
                <PlatformBreakdown metrics={metrics} />
                <details className="page__details">
                  <summary className="page__details-summary">
                    {t('dashboard.platform.tableToggle')}
                  </summary>
                  <DataTable
                    caption={t('dashboard.platform.title')}
                    columns={platformColumns}
                    rows={metrics}
                    rowKey={row => row.metrickey ?? row.metricname ?? ''}
                    status="ready"
                  />
                </details>
              </>
            ) : (
              <p className="page__note">{t('dashboard.platform.empty')}</p>
            )}
          </Card>

          <Card
            subtitle={t('dashboard.sections.audienceSubtitle')}
            title={t('dashboard.sections.audience')}>
            <div className="dash-infos">
              {AUDIENCE.map(tile => {
                const value = data[tile.key];
                const share = data.allUsers > 0 ? value / data.allUsers : undefined;
                return (
                  <InfoTile
                    key={tile.key}
                    icon={tile.icon}
                    label={t(`dashboard.metrics.${tile.key}`)}
                    share={share}
                    shareCaption={
                      share === undefined
                        ? undefined
                        : t('dashboard.shareOfAllUsers', {
                            percent: Math.round(share * 100),
                          })
                    }
                    value={formatCount(value)}
                  />
                );
              })}
            </div>
          </Card>

          <Card
            subtitle={t('dashboard.sections.engagementSubtitle')}
            title={t('dashboard.sections.engagement')}>
            <div className="dash-infos">
              {ENGAGEMENT.map(tile => (
                <InfoTile
                  key={tile.key}
                  icon={tile.icon}
                  label={t(`dashboard.metrics.${tile.key}`)}
                  value={formatCount(data[tile.key])}
                />
              ))}
              <InfoTile
                icon="clock"
                label={t('dashboard.metrics.timeOnlineInDateRange')}
                value={data.timeOnlineInDateRange || t('common.none')}
              />
            </div>
          </Card>

          <Card
            subtitle={t('dashboard.sections.allTimeSubtitle')}
            title={t('dashboard.sections.allTime')}>
            <div className="dash-infos">
              {ALL_TIME.map(tile => (
                <InfoTile
                  key={tile.key}
                  icon={tile.icon}
                  label={t(`dashboard.metrics.${tile.key}`)}
                  value={formatCount(data[tile.key])}
                />
              ))}
            </div>
          </Card>

          <Card
            subtitle={t('dashboard.shortcuts.subtitle')}
            title={t('dashboard.shortcuts.title')}>
            <div className="page__shortcuts">
              {SHORTCUTS.map(shortcut => (
                <Link
                  className="ui-button ui-button--secondary ui-button--small"
                  key={shortcut.to}
                  to={shortcut.to}>
                  {t(shortcut.labelKey)}
                </Link>
              ))}
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
